import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { getProjects, getTasks } from '../api';
import NewProjectModal from '../components/NewProjectModal';
import QuickStartWizard from '../components/QuickStartWizard';
import { IconAIAgent } from '../components/Icons';
import { decodeEntities } from '../utils/decodeEntities';

export default function DashboardPage() {
  const { user }      = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => { const h = () => setIsMobile(window.innerWidth <= 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  const navigate      = useNavigate();
  const [projects, setProjects] = useState([]);
  const [tasks,    setTasks]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [showAiUpgrade,  setShowAiUpgrade]  = useState(false);

  const canUseAiAgent = () => {
    try {
      const stored = JSON.parse(localStorage.getItem('gpf_user') || '{}');
      const role = stored?.role || '';
      if (role === 'administrator') return true;
      const cached = JSON.parse(localStorage.getItem('gpf_plan_features') || '{}');
      const workspace = JSON.parse(localStorage.getItem('gpf_workspace_cache') || '{}');
      const plan = workspace?.plan || 'free';
      return cached?.[plan]?.ai_agent === true;
    } catch { return false; }
  };

  const handleQuickStartClick = () => {
    if (!canUseAiAgent()) { setShowAiUpgrade(true); return; }
    setShowQuickStart(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pRes, tRes] = await Promise.all([getProjects(), getTasks()]);
        setProjects(pRes.data);
        setTasks(tRes.data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const refreshData = async () => {
    try {
      const [pRes, tRes] = await Promise.all([getProjects(), getTasks()]);
      setProjects(pRes.data);
      setTasks(tRes.data);
    } catch (err) { console.error(err); }
  };

  const today          = new Date().toISOString().split('T')[0];
  const totalProjects  = projects.length;
  const totalTasks     = tasks.length;
  const overdueTasks   = tasks.filter(t => t.meta?.gpf_end_date && t.meta.gpf_end_date < today && t.meta?.gpf_status !== 'completed').length;
  const completedTasks = tasks.filter(t => t.meta?.gpf_status === 'completed').length;

  const stats = [
    { label: 'Total Projects', value: totalProjects,  accent: '#2563eb', path: '/projects',           filter: null },
    { label: 'Total Tasks',    value: totalTasks,     accent: '#0891b2', path: '/projects',           filter: null },
    { label: 'Overdue Tasks',  value: overdueTasks,   accent: '#ef4444', path: null,                  filter: null },
    { label: 'Completed Tasks',value: completedTasks, accent: '#16a34a', path: null,                  filter: null },
  ];

  const recentProjects = [...projects].slice(0, 5);

  const statusColor = (status) => {
    switch (status) {
      case 'active':    return { bg: '#eff6ff', text: '#2563eb' };
      case 'completed': return { bg: '#f0fdf4', text: '#16a34a' };
      case 'archived':  return { bg: '#f8fafc', text: '#64748b' };
      default:          return { bg: '#eff6ff', text: '#2563eb' };
    }
  };

  return (
    <Layout>
      <div style={styles.page}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.greeting}>Hey {user?.name?.split(' ')[0] || 'there'} 👋</h1>
            <p style={styles.subheading}>Here's what's happening with your projects today.</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={styles.quickStartBtn} onClick={handleQuickStartClick}>
              🚀 Quick Start
            </button>
            <button style={styles.newProjectBtn} onClick={() => setShowNewProject(true)}>
              + New Project
            </button>
          </div>
        </div>

        {loading ? (
          <p style={styles.loading}>Loading...</p>
        ) : (
          <>
            {/* Stats */}
            <div style={styles.statsGrid}>
              <div
                style={{ ...styles.statCard, borderTop: '4px solid #2563eb', cursor: 'pointer' }}
                onClick={() => navigate('/projects')}
                title="View all projects"
              >
                <span style={{ ...styles.statValue, color: '#2563eb' }}>{totalProjects}</span>
                <span style={styles.statLabel}>Total Projects</span>
                <span style={styles.statHint}>Click to view →</span>
              </div>

              <div
                style={{ ...styles.statCard, borderTop: '4px solid #0891b2', cursor: 'pointer' }}
                onClick={() => navigate('/projects')}
                title="View all tasks"
              >
                <span style={{ ...styles.statValue, color: '#0891b2' }}>{totalTasks}</span>
                <span style={styles.statLabel}>Total Tasks</span>
                <span style={styles.statHint}>Across all projects →</span>
              </div>

              <div
                style={{ ...styles.statCard, borderTop: '4px solid #ef4444', cursor: overdueTasks > 0 ? 'pointer' : 'default' }}
                onClick={() => overdueTasks > 0 && navigate('/reports')}
                title="View overdue tasks in Reports"
              >
                <span style={{ ...styles.statValue, color: '#ef4444' }}>{overdueTasks}</span>
                <span style={styles.statLabel}>Overdue Tasks</span>
                <span style={{ ...styles.statHint, color: overdueTasks > 0 ? '#ef4444' : '#94a3b8' }}>
                  {overdueTasks > 0 ? 'View in Reports →' : 'All on track ✓'}
                </span>
              </div>

              <div
                style={{ ...styles.statCard, borderTop: '4px solid #16a34a', cursor: 'pointer' }}
                onClick={() => navigate('/reports')}
                title="View completed tasks"
              >
                <span style={{ ...styles.statValue, color: '#16a34a' }}>{completedTasks}</span>
                <span style={styles.statLabel}>Completed Tasks</span>
                <span style={styles.statHint}>View in Reports →</span>
              </div>
            </div>

            {/* Recent Projects */}
            <div style={styles.section}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>Recent Projects</h2>
                <button style={styles.viewAll} onClick={() => navigate('/projects')}>View all →</button>
              </div>

              {recentProjects.length === 0 ? (
                <div style={styles.empty}>
                  <p style={styles.emptyText}>No projects yet.</p>
                  <button style={styles.newProjectBtn} onClick={() => setShowNewProject(true)}>
                    Create your first project
                  </button>
                </div>
              ) : (
                <div style={styles.projectList}>
                  {recentProjects.map((project) => {
                    const status   = project.meta?.gpf_status || 'active';
                    const colors   = statusColor(status);
                    const progress = project.meta?.gpf_progress || 0;
                    const dept     = project.meta?.gpf_department || '';
                    return (
                      <div key={project.id} style={styles.projectRow} onClick={() => navigate(`/projects/${project.id}/tasks`)}>
                        <div style={styles.projectInfo}>
                          <div style={styles.projectNameRow}>
                            <span style={styles.projectName}>{decodeEntities(project.title?.rendered)?.toUpperCase() || 'UNTITLED'}</span>
                            {dept && <span style={styles.deptBadge}>{dept}</span>}
                          </div>
                          <div style={styles.progressBar}>
                            <div style={{ ...styles.progressFill, width: `${progress}%` }} />
                          </div>
                        </div>
                        <div style={styles.projectMeta}>
                          <span style={{ ...styles.badge, background: colors.bg, color: colors.text }}>{status}</span>
                          <span style={styles.progressLabel}>{progress}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showNewProject && (
        <NewProjectModal
          onClose={() => setShowNewProject(false)}
          onCreated={(project) => { refreshData(); navigate(`/projects/${project.id}/tasks`); }}
        />
      )}

      {showAiUpgrade && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 36, maxWidth: 400, width: '100%', textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.25)' }}>
            <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'center' }}><div style={{ width: 64, height: 64, borderRadius: 16, background: 'linear-gradient(135deg, #7c3aed, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}><IconAIAgent width={30} height={30} /></div></div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: '0 0 10px' }}>AI Agent Not Available</h2>
            <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, margin: '0 0 24px' }}>
              Quick Start uses our AI Agent to build your project plan. This feature is included in the Business plan.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                style={{ padding: '12px 24px', background: '#fb923c', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
                onClick={() => { setShowAiUpgrade(false); navigate('/workspace?upgrade=1'); }}
              >Upgrade Plan →</button>
              <button
                style={{ padding: '11px 24px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', color: '#374151' }}
                onClick={() => setShowAiUpgrade(false)}
              >Maybe Later</button>
            </div>
          </div>
        </div>
      )}

      {showQuickStart && (
        <QuickStartWizard
          onClose={() => setShowQuickStart(false)}
          onCreated={() => refreshData()}
        />
      )}
    </Layout>
  );
}

const styles = {
  page:         { maxWidth: 900 },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 },
  greeting:     { fontSize: 26, fontWeight: 700, color: '#0f172a', margin: 0 },
  subheading:   { color: '#64748b', fontSize: 14, margin: '6px 0 0' },
  newProjectBtn:{ padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  quickStartBtn:{ padding: '10px 20px', background: 'white', color: '#2563eb', border: '1.5px solid #bfdbfe', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  loading:      { color: '#64748b', fontSize: 14 },
  statsGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 },
  statCard:     { background: '#fff', borderRadius: 10, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'box-shadow 0.15s' },
  statValue:    { fontSize: 32, fontWeight: 700 },
  statLabel:    { fontSize: 13, color: '#64748b', fontWeight: 500 },
  statHint:     { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  section:      { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  sectionHeader:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 },
  viewAll:      { background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  projectList:  { display: 'flex', flexDirection: 'column', gap: 12 },
  projectRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderRadius: 8, border: '1px solid #f1f5f9', cursor: 'pointer', background: '#fafafa', flexWrap: 'wrap', gap: 8 },
  projectInfo:  { flex: 1, marginRight: 24 },
  projectNameRow:{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  projectName:  { fontSize: 14, fontWeight: 600, color: '#0f172a' },
  deptBadge:    { fontSize: 11, fontWeight: 700, color: '#7c3aed', background: '#f5f3ff', padding: '2px 8px', borderRadius: 99 },
  progressBar:  { height: 4, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' },
  progressFill: { height: '100%', background: '#2563eb', borderRadius: 99 },
  projectMeta:  { display: 'flex', alignItems: 'center', gap: 12 },
  badge:        { padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' },
  progressLabel:{ fontSize: 13, color: '#64748b', fontWeight: 600, minWidth: 36, textAlign: 'right' },
  empty:        { textAlign: 'center', padding: '32px 0' },
  emptyText:    { color: '#94a3b8', fontSize: 14, marginBottom: 16 },
};